import axios from 'axios';
import { getDeviceHeaders } from '../utils/deviceInfo';
import { SUPPORTED_LANG_CODES, DEFAULT_LANGUAGE } from '../i18n/config';

let getStoreState: (() => any) | null = null;

export const injectStore = (getState: () => any) => {
  getStoreState = getState;
};

const axiosInstance = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? '/' : process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'x-tenant-id': process.env.REACT_APP_TENANT_ID || '1',
  },
});

// Dedicated client for token refresh to prevent recursive interceptor loops and uncoordinated state leaks
const authRefreshClient = axios.create({
  baseURL: process.env.NODE_ENV === 'development' ? '/' : process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'x-tenant-id': process.env.REACT_APP_TENANT_ID || '1',
  },
});

axiosInstance.interceptors.request.use(
  (config) => {
    let currentLang: string | null = null;

    // Priority 1: user?.language from Redux state (if authenticated)
    if (getStoreState) {
      try {
        const state = getStoreState();
        const auth = state?.auth;
        if (auth?.isAuthenticated) {
          const rawUserLang = auth.user?.language || auth.user?.profile?.language;
          const userLang = rawUserLang ? rawUserLang.toLowerCase().split(/[-_]/)[0] : null;
          if (userLang && (SUPPORTED_LANG_CODES as readonly string[]).includes(userLang)) {
            currentLang = userLang;
          }
        }
      } catch (err) {
        // Fallback gracefully if store read throws
      }
    }

    // Priority 2: URL pathname (/:lang) if guest
    if (!currentLang && typeof window !== 'undefined') {
      const segments = window.location.pathname.split('/').filter(Boolean);
      const firstSegment = segments[0] ? segments[0].toLowerCase().split(/[-_]/)[0] : null;
      if (firstSegment && (SUPPORTED_LANG_CODES as readonly string[]).includes(firstSegment)) {
        currentLang = firstSegment;
      }
    }

    // Priority 3: localStorage or DEFAULT_LANGUAGE fallback
    if (!currentLang && typeof window !== 'undefined') {
      const saved = localStorage.getItem('language');
      const normalizedSaved = saved ? saved.toLowerCase().split(/[-_]/)[0] : null;
      if (
        normalizedSaved &&
        (SUPPORTED_LANG_CODES as readonly string[]).includes(normalizedSaved)
      ) {
        currentLang = normalizedSaved;
      }
    }

    if (!currentLang) {
      currentLang = DEFAULT_LANGUAGE;
    }

    if (config.headers) {
      config.headers['x-language'] = config.headers['x-language'] || currentLang;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Variables to handle multiple parallel 401s
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        isRefreshing = true;

        try {
          const headers = await getDeviceHeaders();
          await authRefreshClient.post('/client/auth/refresh', {}, { headers });
          isRefreshing = false;
          processQueue(null);
          return axiosInstance(originalRequest);
        } catch (refreshError: any) {
          isRefreshing = false;
          processQueue(refreshError);
          if (refreshError.response?.status === 401) {
            localStorage.setItem('isLogin', 'false');
            window.location.href = '/';
          }
          return Promise.reject(refreshError);
        }
      }
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
